import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { mobileApi } from '../../services/api';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import LocateMap from '../../components/mobile/LocateMap';
import {
  MapPin, ChevronRight, Loader2, X, Navigation, ClipboardCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const relTime = (iso) => {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
};
const isStale = (iso) => iso && (Date.now() - new Date(iso).getTime()) > 2 * 3600 * 1000;

const MobileAssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useMobileAuth();
  const [loading, setLoading] = useState(true);
  const [asset, setAsset] = useState(null);
  const [locate, setLocate] = useState(false);
  const [livePos, setLivePos] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showReading, setShowReading] = useState(false);
  const [reading, setReading] = useState('');
  const [showSpec, setShowSpec] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await mobileApi.getAsset(id, getToken()); setAsset(r.data); }
    catch { toast.error('Failed to load asset'); navigate(-1); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Live position: refresh every 30s while the Locate sheet is open.
  useEffect(() => {
    if (!locate) return;
    let active = true;
    const poll = async () => {
      setRefreshing(true);
      try {
        const r = await mobileApi.getAssetPosition(id, getToken());
        if (active && r.data?.last_position) setLivePos(r.data.last_position);
      } catch { /* keep last */ }
      finally { if (active) setRefreshing(false); }
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => { active = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locate, id]);

  const saveReading = async () => {
    if (!reading.trim()) return;
    setBusy(true);
    try { await mobileApi.saveAssetReading(id, parseFloat(reading), getToken()); setShowReading(false); setReading(''); await load(); toast.success('Reading saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save reading'); }
    finally { setBusy(false); }
  };
  const startInspection = async () => {
    try {
      const r = await mobileApi.getInspectionsDue(getToken());
      const tpl = (r.data || [])[0]?.template?.id;
      if (tpl) navigate(`/mobile/inspections/new/${id}/${tpl}`);
      else toast.error('No inspection template available');
    } catch { toast.error('Could not start inspection'); }
  };
  const raiseWO = async () => {
    setBusy(true);
    try { const r = await mobileApi.raiseAssetWorkOrder(id, getToken()); toast.success(`${r.data.reference} raised & assigned to you`); navigate(`/mobile/work-orders/${r.data.work_order_id}`); }
    catch { toast.error('Failed to raise work order'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-[var(--surface-page)] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[var(--volt-icon)] animate-spin" /></div>;
  if (!asset) return null;

  const assetId = asset.unit_number || asset.name || '—';
  const model = [asset.make, asset.model].filter(Boolean).join(' ') || asset.name;
  const statusLabel = (asset.status || '').replace(/_/g, ' ');
  const inService = asset.status === 'active';
  const attention = asset.needs_attention || [];
  const hm = asset.hour_meter;
  const cert = asset.certificate_summary;
  const pos = livePos || asset.last_position;
  const hasDevice = asset.asset_has_device;
  const posStale = pos && isStale(pos.updated_at);

  const routeAttention = (a) => {
    if (a.kind === 'defect') navigate(`/mobile/defects/${a.id}`);
    else if (a.kind === 'work_order') navigate(`/mobile/work-orders/${a.id}`);
    else startInspection();
  };

  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-40" data-testid="asset-detail">
      <MobileHeader showBack
        title={<span className="flex flex-col leading-tight">
          <span className="font-mono text-[14px] font-bold text-[var(--ink-primary)] truncate">{assetId}</span>
          <span className="text-[12px] font-normal text-[var(--ink-tertiary)] truncate">{model}</span>
        </span>} />

      <div className="px-4 py-4 space-y-4">
        {/* Identity */}
        <MobileCard padding="none">
          <div className="flex items-center gap-3 p-4">
            <div className="shrink-0 rounded-[10px] flex items-center justify-center text-[9px] font-bold text-[var(--ink-tertiary)] text-center leading-tight" style={{ width: 56, height: 56, background: 'var(--surface-sunken)', border: '1px solid var(--border)' }}>ASSET<br/>PHOTO</div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-[var(--ink-primary)] truncate">{model}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[11px] font-semibold border" style={inService ? { background: 'var(--pass-bg)', color: 'var(--pass-ink)', borderColor: 'var(--pass-edge)' } : { background: 'var(--overdue-bg)', color: 'var(--overdue-ink)', borderColor: 'var(--overdue-edge)' }}>{inService ? 'In service' : statusLabel}</span>
                {asset.location && <span className="text-[12.5px] text-[var(--ink-secondary)] truncate">{asset.location}</span>}
              </div>
            </div>
          </div>
          {hasDevice && (
            <button onClick={() => setLocate(true)} data-testid="asset-locate-row"
              className="w-full flex items-center gap-2 px-4 border-t border-[var(--divider)]" style={{ height: 48 }}>
              <MapPin className="w-4 h-4 text-[var(--volt-icon)]" />
              <span className="text-[13.5px] font-semibold text-[var(--volt-ink)]">Locate asset</span>
              <span className="ml-auto font-mono text-[10.5px]" style={{ color: posStale ? '#9A3200' : 'var(--ink-tertiary)' }}>{pos ? relTime(pos.updated_at) : 'No position reported'}</span>
              <ChevronRight className="w-4 h-4 text-[var(--ink-tertiary)]" />
            </button>
          )}
        </MobileCard>

        {/* Needs attention */}
        {attention.length > 0 ? (
          <MobileCard spine="var(--critical)" padding="none">
            <div className="flex items-center gap-2 px-4 bg-[var(--surface-sunken)] rounded-t-[13px]" style={{ height: 34 }}>
              <span className="text-[11px] font-bold uppercase tracking-[0.11em]" style={{ color: '#8E1212' }}>Needs attention</span>
              <span className="font-mono text-[11px] px-1.5 rounded-[4px]" style={{ background: 'var(--critical-bg)', color: 'var(--critical-ink)' }}>{attention.length}</span>
            </div>
            <div className="px-4">
              {attention.map((a, i) => (
                <button key={i} onClick={() => routeAttention(a)} data-testid={`asset-attention-${i}`}
                  className={`w-full flex items-center gap-3 text-left ${i ? 'border-t border-[var(--divider)]' : ''}`} style={{ minHeight: 48 }}>
                  <span className="shrink-0 rounded-full" style={{ width: 6, height: 6, background: a.dot }} />
                  <div className="min-w-0 flex-1 py-2">
                    <div className="text-[14.5px] font-semibold text-[var(--ink-primary)] truncate">{a.title}</div>
                    <div className="text-[12.5px] text-[var(--ink-tertiary)] truncate">{a.sub}</div>
                  </div>
                  <span className="shrink-0 text-[13px] font-semibold" style={{ color: a.dot === '#E0B800' ? 'var(--volt-ink)' : a.dot === '#C23A00' ? 'var(--overdue-ink)' : 'var(--critical-ink)' }}>{a.age_text}</span>
                  <ChevronRight className="w-4 h-4 text-[var(--ink-tertiary)] shrink-0" />
                </button>
              ))}
            </div>
          </MobileCard>
        ) : (
          <MobileCard padding="normal"><p className="text-[15px] font-semibold text-[var(--ink-secondary)]">Nothing outstanding on this asset</p></MobileCard>
        )}

        {/* Hour meter */}
        {hm && (
          <MobileCard padding="normal">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-[0.11em] text-[var(--ink-tertiary)]">Hour meter</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-mono text-[22px] font-bold text-[var(--ink-primary)]">{Number(hm.value).toLocaleString()}</span>
                  <span className="text-[13px] text-[var(--ink-secondary)]">{hm.unit}</span>
                </div>
                <div className="text-[12px] text-[var(--ink-tertiary)] mt-0.5">read {relTime(hm.last_read_at)}{asset.hours_read_by ? ` · ${asset.hours_read_by}` : ''}</div>
              </div>
              <button onClick={() => { setShowReading(true); setReading(String(hm.value)); }} data-testid="asset-enter-reading" className="text-[13px] font-semibold text-[var(--volt-ink)] shrink-0">Enter reading</button>
            </div>
            {showReading && (
              <div className="flex gap-2 mt-3 pt-3 border-t border-[var(--divider)]">
                <input type="number" inputMode="decimal" value={reading} onChange={(e) => setReading(e.target.value)} autoFocus
                  data-testid="asset-reading-input"
                  className="flex-1 h-11 rounded-[10px] bg-white border border-[var(--border-strong)] px-3 font-mono text-[16px] text-[var(--ink-primary)] outline-none focus:border-[#1A1A1A] focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]" />
                <button onClick={saveReading} disabled={busy || !reading.trim()} data-testid="asset-reading-save" className="h-11 px-4 rounded-[10px] bg-[var(--volt)] text-black text-[13px] font-bold disabled:opacity-40" style={{ border: '1px solid var(--volt-edge)' }}>Save</button>
                <button onClick={() => setShowReading(false)} className="h-11 w-11 rounded-[10px] flex items-center justify-center" style={{ border: '1px solid var(--border-strong)' }}><X className="w-4 h-4 text-[var(--ink-tertiary)]" /></button>
              </div>
            )}
          </MobileCard>
        )}

        {/* Records */}
        <MobileCard padding="none">
          {cert && (
            <button onClick={() => toast.info(`${cert.name}${cert.days != null ? ` expires in ${cert.days} days` : ''}`)}
              className="w-full flex items-center justify-between px-4 border-b border-[var(--divider)]" style={{ height: 48 }}>
              <span className="text-[14px] font-semibold text-[var(--ink-primary)]">Certificates</span>
              <span className="flex items-center gap-1.5">
                <span className="text-[12.5px]" style={{ color: cert.days != null && cert.days <= 30 ? '#6B5600' : 'var(--ink-tertiary)' }}>{cert.name}{cert.days != null ? ` expires in ${cert.days} days` : ''}</span>
                <ChevronRight className="w-4 h-4 text-[var(--ink-tertiary)]" />
              </span>
            </button>
          )}
          <button onClick={() => setShowSpec((s) => !s)} data-testid="asset-spec-row" className="w-full flex items-center justify-between px-4" style={{ height: 48 }}>
            <span className="text-[14px] font-semibold text-[var(--ink-primary)]">Specification</span>
            <span className="flex items-center gap-1.5">
              <span className="text-[12.5px] text-[var(--ink-tertiary)]">Serial, make, model, year</span>
              <ChevronRight className={`w-4 h-4 text-[var(--ink-tertiary)] transition-transform ${showSpec ? 'rotate-90' : ''}`} />
            </span>
          </button>
          {showSpec && (
            <div className="px-4 pb-3 border-t border-[var(--divider)] divide-y divide-[var(--divider)]">
              {[['Serial', asset.serial_number], ['Make', asset.make], ['Model', asset.model], ['Year', asset.year]].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-2.5">
                  <span className="text-[12.5px] text-[var(--ink-tertiary)]">{k}</span>
                  <span className="font-mono text-[13px] text-[var(--ink-primary)]">{v || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </MobileCard>
      </div>

      {/* Bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pt-2.5 pb-4 bg-[var(--surface-page)]" style={{ boxShadow: 'var(--elev-floating)' }}>
        <button onClick={startInspection} data-testid="asset-start-inspection"
          className="w-full rounded-[13px] bg-[var(--volt)] text-black flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
          style={{ height: 56, fontWeight: 800, border: '1px solid var(--volt-edge)' }}>
          <ClipboardCheck className="w-5 h-5" /> START INSPECTION
        </button>
        <div className="flex items-center justify-center gap-6 mt-1">
          <button onClick={() => navigate(`/mobile/defects/new?asset=${id}`)} data-testid="asset-report-defect" className="h-11 text-[13px] font-semibold text-[var(--volt-ink)]">Report defect</button>
          <button onClick={raiseWO} data-testid="asset-raise-wo" className="h-11 text-[13px] font-semibold text-[var(--volt-ink)]">Raise work order</button>
        </div>
      </div>

      {/* Locate sheet (7b) */}
      {locate && (
        <div className="fixed inset-0 z-50 flex items-end" style={{ background: 'rgba(26,26,26,0.5)' }} onClick={() => setLocate(false)} data-testid="asset-locate-sheet">
          <div className="w-full bg-[var(--surface-card)] rounded-t-[18px] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-[var(--border-strong)] mx-auto mb-4" />
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-[17px] font-bold text-[var(--ink-primary)]">Last known position</h3>
                <p className="text-[12.5px] text-[var(--ink-tertiary)] mt-0.5">{assetId}{asset.telemetry_imei ? ` · Telematics unit ${String(asset.telemetry_imei).slice(-4)}` : ''}</p>
              </div>
              <button onClick={() => setLocate(false)}><X className="w-5 h-5 text-[var(--ink-tertiary)]" /></button>
            </div>
            <div className="rounded-[12px] overflow-hidden relative" style={{ height: 190, background: 'var(--surface-sunken)', border: '1px solid var(--border)' }}>
              {pos ? (
                <LocateMap lat={pos.latitude} lng={pos.longitude} />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                  <MapPin className="w-7 h-7 text-[var(--ink-tertiary)]" />
                  <span className="text-[12px] text-[var(--ink-tertiary)]">No position reported</span>
                </div>
              )}
              {refreshing && (
                <span className="absolute top-2 right-2 z-[500] flex items-center gap-1 bg-white/90 rounded-full px-2 py-0.5 text-[10.5px] font-semibold text-[var(--ink-secondary)]" style={{ border: '1px solid var(--border)' }}>
                  <Loader2 className="w-3 h-3 animate-spin" /> Refreshing
                </span>
              )}
            </div>
            <div className="rounded-[12px] mt-4 divide-y divide-[var(--divider)]" style={{ border: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between px-4 py-2.5"><span className="text-[13px] text-[var(--ink-secondary)]">Fix taken</span><span className="text-[13px] font-semibold text-[var(--ink-primary)]">{pos ? relTime(pos.updated_at) : '—'}</span></div>
              <div className="flex items-center justify-between px-4 py-2.5"><span className="text-[13px] text-[var(--ink-secondary)]">Coordinates</span><span className="font-mono text-[12.5px] text-[var(--ink-primary)]">{pos ? `${pos.latitude.toFixed(4)}, ${pos.longitude.toFixed(4)}` : '—'}</span></div>
            </div>
            <button onClick={() => pos && window.open(`https://www.google.com/maps/search/?api=1&query=${pos.latitude},${pos.longitude}`, '_blank')}
              disabled={!pos} data-testid="asset-open-maps"
              className="w-full mt-4 rounded-[13px] bg-white flex items-center justify-center gap-2 disabled:opacity-40" style={{ height: 52, border: '1.5px solid #1A1A1A' }}>
              <Navigation className="w-[18px] h-[18px] text-[var(--ink-primary)]" />
              <span className="text-[14px] font-semibold text-[var(--ink-primary)]">Open in Maps</span>
            </button>
            <p className="text-[12px] text-[var(--ink-tertiary)] mt-2 leading-snug">Hands off to your phone's maps app for turn-by-turn. Guardian does not navigate.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileAssetDetail;
